<div class="message-details-container">
  <main class="chat-feed-container">
    <app-conversation-header [subject]="headerSubject()"
                             [messageId]="message?.message?.id"
                             [chips]="headerChips()"
                             (back)="location.back()"></app-conversation-header>

    @if (replyTarget(); as replyMessage) {
    <section class="reply-panel">
      <form [formGroup]="replyForm"
            (ngSubmit)="sendReply()">
        <header class="reply-panel-header">
          <span class="reply-panel-title">
            {{ 'message.reply.replyTo' | translate }} <strong>{{ getContactDisplay(replyMessage) }}</strong>
          </span>
          <div class="reply-panel-actions">
            <ecab-select [options]="getLanguageOptions()"
                         formControlName="language"
                         class="lang-select"></ecab-select>
            <button type="button"
                    class="reply-panel-close"
                    (click)="closeReply()">
              <mat-icon>close</mat-icon>
              <span>{{ 'message.reply.close' | translate }}</span>
            </button>
          </div>
        </header>

        <app-markdown-editor formControlName="message"
                             class="content-editor"
                             [placeholder]="'message.reply.hint' | translate"
                             [minHeight]="'120px'"></app-markdown-editor>

        <div class="reply-panel-footer">
          <ecab-button type="submit"
                       variant="primary"
                       [label]="'message.details.send' | translate"
                       [disabled]="replyForm.invalid"></ecab-button>
        </div>
      </form>
    </section>
    }

    <div class="feed-scroll"
         #scrollContainer>
      <div class="feed-lane">
      @for (group of groupedMessages(); track group.key) {
      <div class="day-separator">
        <span>{{ group.label }}</span>
      </div>

      @for (msg of group.messages; track msg.id) {
      <app-message-card [id]="'msg-' + msg.id"
                        [message]="msg"
                        [selectedAttachmentIds]="selectedAttachmentIds()"
                        [blockedAttachmentIds]="icareBlockedAttachmentIds()"
                        [collaborators]="collaboratorOptions()"
                        (openAttachment)="openAttachment($event)"
                        (toggleAttachment)="toggleAttachmentSelection($event)"
                        (selectAllToggle)="toggleSelectAllForMessage($event)"
                        (sendToIcare)="openIcareDialog($event)"
                        (reply)="openReply($event)"
                        (transfer)="openTransferDialog($event)"
                        (assignCollaborator)="onAssignCollaborator($event)"
                        (changeStatus)="onChangeStatus($event)"></app-message-card>
      }
      }
      </div>
    </div>
  </main>
</div>

@if (transferMessage(); as transferMsg) {
<app-transfer-dialog [message]="transferMsg"
                     (close)="closeTransferDialog()"
                     (saved)="onContextSaved()"></app-transfer-dialog>
}

@if (attachmentPreview(); as preview) {
<div class="attachment-preview-backdrop"
     (click)="closeAttachmentPreview($event)">
  <section class="attachment-preview-dialog"
           (click)="$event.stopPropagation()">
    <header class="attachment-preview-header">
      <div class="attachment-preview-heading">
        <span class="attachment-preview-kicker">{{ 'message.details.preview.kicker' | translate }}</span>
        <h2>{{ 'message.details.preview.title' | translate }}</h2>
        <h3>{{ preview.title }}</h3>
        @if (preview.subtitle) {
        <p>{{ preview.subtitle }}</p>
        }
      </div>
      <button type="button"
              class="attachment-preview-close"
              [attr.aria-label]="'message.details.preview.close' | translate"
              [attr.title]="'message.details.preview.close' | translate"
              (click)="closeAttachmentPreview($event)">
        <mat-icon>close</mat-icon>
      </button>
    </header>

    <div class="attachment-preview-content">
      @if (preview.kind === 'image') {
      <img class="attachment-preview-image"
           [src]="preview.sourceUrl"
           [alt]="preview.title">
      } @else if (preview.kind === 'pdf') {
      <iframe class="attachment-preview-pdf"
              [src]="preview.safeSourceUrl"
              [title]="preview.title"></iframe>
      } @else {
      <div class="attachment-preview-empty">
        <mat-icon>hide_image</mat-icon>
        <p>{{ 'message.details.preview.unavailable' | translate }}</p>
        <span>{{ 'message.details.preview.unavailableDescription' | translate }}</span>
      </div>
      }
    </div>

    <footer class="attachment-preview-footer">
      <ecab-button variant="secondary"
                   [label]="'message.details.preview.close' | translate"
                   (clicked)="closeAttachmentPreview($event)"></ecab-button>
    </footer>
  </section>
</div>
}

@if (icareDialogMessage(); as icareMessage) {
<div class="icare-dialog-backdrop"
     (click)="closeIcareDialog()">
  <section class="icare-dialog"
           (click)="$event.stopPropagation()">
    <header class="icare-dialog-header">
      <div class="icare-dialog-heading">
        <span class="icare-dialog-kicker">{{ 'message.details.attachmentsRow.sendToIcare' | translate }}</span>
        <h2>{{ 'message.details.icareDialog.title' | translate }}</h2>
      </div>
      <button type="button"
              class="icare-dialog-close"
              [attr.aria-label]="'message.details.preview.close' | translate"
              (click)="closeIcareDialog()">
        <mat-icon>close</mat-icon>
      </button>
    </header>

    <p class="icare-dialog-description">{{ 'message.details.icareDialog.description' | translate }}</p>

    <div class="icare-dialog-list">
      @for (attachment of icareDialogAttachments(); track attachment.id) {
      <div class="icare-dialog-item">
        <div class="icare-dialog-item-head">
          <mat-icon>{{ getAttachmentIcon(attachment) }}</mat-icon>
          <span class="icare-dialog-item-name">{{ getAttachmentLabel(attachment) }}</span>
          @if (getSelectedIcareDocumentType(attachment.id)) {
          <mat-icon class="icare-dialog-item-check">check_circle</mat-icon>
          }
        </div>
        <ecab-select [ngModel]="getSelectedIcareDocumentType(attachment.id)"
                     [ngModelOptions]="{ standalone: true }"
                     [placeholder]="'message.details.preview.documentTypePlaceholder' | translate"
                     [options]="icareDocumentTypeOptions()"
                     (selectionChange)="onIcareDocumentTypeChange(attachment.id, $any($event))"></ecab-select>
        <ecab-input [ngModel]="getSelectedIcareComment(attachment.id)"
                    [ngModelOptions]="{ standalone: true }"
                    [placeholder]="'message.details.icareDialog.commentPlaceholder' | translate"
                    [attr.aria-label]="'message.details.icareDialog.commentLabel' | translate"
                    (ngModelChange)="onIcareCommentChange(attachment.id, $any($event))"></ecab-input>
      </div>
      }
    </div>

    <footer class="icare-dialog-footer">
      <span class="icare-dialog-progress">
        {{ 'message.details.icareDialog.qualifiedCount' | translate:{ qualified: icareQualifiedCount(), total: icareDialogAttachments().length } }}
      </span>
      <div class="icare-dialog-actions">
        <ecab-button variant="secondary"
                     [label]="'message.details.icareDialog.cancel' | translate"
                     (clicked)="closeIcareDialog()"></ecab-button>
        <ecab-button variant="primary"
                     [label]="'message.details.attachmentsRow.sendToIcare' | translate"
                     [disabled]="!canConfirmIcareForward()"
                     (clicked)="confirmIcareForward()"></ecab-button>
      </div>
    </footer>
  </section>
</div>
}
